// src/components/modals/PosInvoiceModal.jsx
import React, { useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { invoiceAPI, clinicAPI } from '../../services/api'
import { pdfServices } from '../../services/pdfservices'
import PhoneInputWithCountryCode from '../common/PhoneInputWithCountryCode'

const OTHER_ID_SCHEMES = ['TIN', 'CRN', 'MOM', 'MLS', '700', 'SAG', 'NAT', 'GCC', 'IQA', 'PAS', 'OTH']

const emptyItem = () => ({ description: '', qty: '1', unit_price: '', vat_rate: '0', discount_percent: '0', discount_amount: '0' })

// Strict numeric entry: digits + one decimal point, no e/E/+/-/scientific
// notation (type="number" alone still admits those).
const NUMERIC_KEYS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End']
const blockNonNumericKey = (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return // let copy/paste/shortcuts through (paste filtered below)
  if (!NUMERIC_KEYS.includes(e.key)) e.preventDefault()
}
const blockNonNumericPaste = (e) => {
  const text = (e.clipboardData || window.clipboardData).getData('text')
  if (!/^\d*\.?\d*$/.test(text)) e.preventDefault()
}
const numericInputHandlers = { onKeyDown: blockNonNumericKey, onPaste: blockNonNumericPaste }

// Searchable picker for the clinic's own service catalog — the receptionist
// picks (or free-types) an item; picking fills price + VAT from the catalog.
const SERVICE_TYPE_KEYS = { medical: 'medical', non_medical: 'nonMedical', medication: 'medication' }

function ServicePicker({ value, onChange, onPick, services, isLoading, disabled, t }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(value)

  useEffect(() => setQuery(value), [value])

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return services.slice(0, 8)
    return services
      .filter((s) => s.name_en.toLowerCase().includes(q) || (s.name_ar || '').includes(query.trim()))
      .slice(0, 8)
  }, [query, services])

  const pick = (s) => {
    onPick(s)
    setOpen(false)
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        value={query}
        disabled={disabled}
        placeholder={t('posInvoice.items.servicePlaceholder', 'Type to search your services…')}
        onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={{ ...inputStyle, width: '100%' }}
      />
      {open && matches.length > 0 && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          insetInlineStart: 0,
          right: 0,
          background: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '10px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
          zIndex: 30,
          maxHeight: '220px',
          overflowY: 'auto',
        }}>
          {matches.map((s) => (
            <button
              key={s.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(s) }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                width: '100%',
                textAlign: 'start',
                padding: '9px 12px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontFamily: '"Red Hat Display", sans-serif',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#F0FDFA')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#111827' }}>{s.label}</span>
                <span style={{ display: 'block', fontSize: '11px', color: '#9CA3AF' }}>
                  {t(`posInvoice.items.types.${SERVICE_TYPE_KEYS[s.service_type] || 'other'}`, s.service_type || '')}
                  {s.unit ? ` · ${s.unit}` : ''}
                </span>
              </span>
              <span style={{ fontSize: '13px', fontWeight: 700, color: '#00b598', whiteSpace: 'nowrap' }}>{s.price}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const emptyBuyer = () => ({
  phone: '',
  name: '',
  companyName: '',
  vatNumber: '',
  notVatRegistered: false,
  otherId: '',
  otherIdScheme: 'CRN',
  address: {
    street: '',
    building_number: '',
    district: '',
    city: '',
    postal_code: '',
    country: 'SA',
  },
})

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '9px 12px',
  border: '1px solid #D1D5DB',
  borderRadius: '8px',
  fontSize: '14px',
  color: '#111827',
  background: '#FFFFFF',
  fontFamily: '"Red Hat Display", sans-serif',
}

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 500,
  color: '#374151',
  marginBottom: '5px',
}

const PosInvoiceModal = ({
  isOpen,
  onClose,
  clinicData,
}) => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir() === 'rtl'

  const [buyerType, setBuyerType] = useState('b2c')
  const [buyer, setBuyer] = useState(emptyBuyer())
  const [items, setItems] = useState([emptyItem()])
  const [recordPayment, setRecordPayment] = useState(false)
  const [payment, setPayment] = useState({ amount: '', payment_type_id: '' })
  const [paymentTypes, setPaymentTypes] = useState([])
  const [loadingPaymentTypes, setLoadingPaymentTypes] = useState(false)
  const [countries, setCountries] = useState([])
  const [services, setServices] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [issuedInvoice, setIssuedInvoice] = useState(null)
  const [printing, setPrinting] = useState(false)

  const clinic = clinicData?.clinic || clinicData || null
  // Subscribed clinics (Clinics toggle + enabled invoice profile) send B2B
  // invoices through the tax-authority clearance/reporting pipeline. B2B is
  // still offered to everyone else as a manual tax invoice with the full
  // buyer block on the PDF — the company needs it for their own tax return,
  // the backend simply never reports it.
  const invoiceSubscribed = !!clinic?.is_invoice_subscribed && !!clinic?.invoice_profile_enabled
  const currency = clinic?.currency || 'USD'
  const clinicId = clinic?.id || clinicData?.clinic_id || null

  useEffect(() => {
    if (isOpen) {
      setBuyerType('b2c')
      setBuyer(emptyBuyer())
      setItems([emptyItem()])
      setRecordPayment(false)
      setPayment({ amount: '', payment_type_id: '' })
      setError('')
      setIssuedInvoice(null)
    }
  }, [isOpen])

  // Clinic service catalog — drives the searchable description picker
  useEffect(() => {
    if (!isOpen || !clinicId || services.length) return
    const fetchServices = async () => {
      try {
        const res = await clinicAPI.getClinicServices(clinicId)
        const list = res.data?.data?.services || []
        // Walk-in retail: non-medical services plus medications (over-the-
        // counter sales). Clinical/medical services still belong on visits.
        setServices(list
          .filter((s) => s.service_type === 'non_medical' || s.service_type === 'medication')
          .map((s) => ({
          id: s.id,
          name_en: s.service_name_en || '',
          name_ar: s.service_name_ar || '',
          label: (i18n.language === 'ar' && s.service_name_ar) ? s.service_name_ar : (s.service_name_en || s.service_name_ar),
          price: s.price,
          vat: s.has_vat ? (s.vat_percentage ?? 0) : 0,
          service_type: s.service_type,
          unit: s.unit,
        })))
      } catch {
        setServices([]) // free typing still works without the catalog
      }
    }
    fetchServices()
  }, [isOpen, clinicId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Payment methods (same endpoint the reception payment modal uses)
  useEffect(() => {
    if (!isOpen || paymentTypes.length) return
    const fetchPaymentTypes = async () => {
      setLoadingPaymentTypes(true)
      try {
        const token = localStorage.getItem('access_token')
        const base = import.meta.env.VITE_API_URL || 'http://localhost:8000'
        const response = await fetch(`${base}/api/v1/payment-types`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            accept: 'application/json',
          },
        })
        if (response.ok) {
          const data = await response.json()
          const list = data?.data || []
          setPaymentTypes(Array.isArray(list) ? list : [])
        }
      } catch (e) {
        console.warn('Failed to load payment types:', e)
      } finally {
        setLoadingPaymentTypes(false)
      }
    }
    fetchPaymentTypes()
  }, [isOpen, paymentTypes.length])

  // Countries for the B2B address (same endpoint CompleteProfile uses)
  useEffect(() => {
    if (!isOpen || countries.length) return
    const fetchCountries = async () => {
      try {
        const base = import.meta.env.VITE_API_URL || 'http://localhost:8000'
        const response = await fetch(`${base}/api/v1/locations/countries-only`)
        if (response.ok) {
          const data = await response.json()
          const list = Array.isArray(data) ? data : (data?.data || [])
          if (list.length) setCountries(list)
        }
      } catch (e) {
        console.warn('Failed to load countries:', e)
      }
    }
    fetchCountries()
  }, [isOpen, countries.length])

  const totals = useMemo(() => {
    let subtotal = 0
    let discount = 0
    let vat = 0
    items.forEach((item) => {
      const qty = parseFloat(item.qty) || 0
      const price = parseFloat(item.unit_price) || 0
      const rate = parseFloat(item.vat_rate) || 0
      const discPct = parseFloat(item.discount_percent) || 0
      const line = qty * price
      const lineDiscount = (line * discPct) / 100
      const taxable = line - lineDiscount
      subtotal += line
      discount += lineDiscount
      vat += (taxable * rate) / 100
    })
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      discount: Math.round(discount * 100) / 100,
      vat: Math.round(vat * 100) / 100,
      total: Math.round((subtotal - discount + vat) * 100) / 100,
    }
  }, [items])

  // Per-line total after discount + VAT (matches the backend math)
  const lineTotal = (item) => {
    const qty = parseFloat(item.qty) || 0
    const price = parseFloat(item.unit_price) || 0
    const rate = parseFloat(item.vat_rate) || 0
    const discPct = parseFloat(item.discount_percent) || 0
    const taxable = qty * price * (1 - discPct / 100)
    return Math.round(taxable * (1 + rate / 100) * 100) / 100
  }

  // Discount dual-input sync: the percentage is canonical (the backend
  // computes the amount from it); the amount field is a display mirror.
  const lineGross = (item) => (parseFloat(item.qty) || 0) * (parseFloat(item.unit_price) || 0)

  const onDiscountPercentChange = (index, value) => {
    const item = items[index]
    const gross = lineGross(item)
    const raw = parseFloat(value)
    const pct = isNaN(raw) ? 0 : Math.min(Math.max(raw, 0), 99.9) // never above 99.9%
    setItems((prev) => prev.map((it, i) => (i === index ? {
      ...it,
      discount_percent: String(pct),
      discount_amount: String(Math.round(gross * pct / 100 * 100) / 100),
    } : it)))
  }

  const onDiscountAmountChange = (index, value) => {
    const item = items[index]
    const gross = lineGross(item)
    const raw = parseFloat(value)
    let amount = isNaN(raw) ? 0 : raw
    // Keep at least 0.1 of the line: amount can't exceed gross − 0.1,
    // and a positive amount can't be below 0.1.
    const maxAmount = Math.max(0, gross - 0.1)
    amount = Math.min(amount, maxAmount)
    if (amount > 0 && amount < 0.1) amount = 0.1
    setItems((prev) => prev.map((it, i) => (i === index ? {
      ...it,
      discount_amount: String(amount),
      discount_percent: gross > 0 ? String(Math.round(amount / gross * 100 * 1000) / 1000) : '0',
    } : it)))
  }

  // Quantity edits shift the gross, so refresh the amount mirror from the
  // (canonical) percentage to keep the two fields in agreement.
  const onQtyChange = (index, value) => {
    const item = items[index]
    const gross = (parseFloat(value) || 0) * (parseFloat(item.unit_price) || 0)
    const pct = parseFloat(item.discount_percent) || 0
    setItems((prev) => prev.map((it, i) => (i === index ? {
      ...it,
      qty: value,
      discount_amount: String(Math.round(gross * pct / 100 * 100) / 100),
    } : it)))
  }

  const lockedFieldStyle = { ...inputStyle, backgroundColor: '#F3F4F6', color: '#6B7280', cursor: 'not-allowed' }

  const updateItem = (index, field, value) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)))
  }

  const addItem = () => setItems((prev) => [...prev, emptyItem()])
  const removeItem = (index) => setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev))

  const setAddressField = (field, value) => {
    setBuyer((prev) => ({ ...prev, address: { ...prev.address, [field]: value } }))
  }

  const validate = () => {
    if (buyerType === 'b2b') {
      if (!buyer.companyName.trim()) {
        return t('posInvoice.errors.companyRequired', 'Company name is required.')
      }
      if (!buyer.phone.trim()) {
        return t('posInvoice.errors.b2bPhoneRequired', 'Contact phone is required for business invoices.')
      }
      if (!buyer.notVatRegistered && !buyer.vatNumber.trim()) {
        return t('posInvoice.errors.vatRequired', 'VAT number is required (or mark the buyer as not VAT-registered).')
      }
      if (buyer.notVatRegistered && (!buyer.otherId.trim() || !buyer.otherIdScheme)) {
        return t('posInvoice.errors.otherIdRequired', 'Buyer ID number and scheme are required.')
      }
      if (!buyer.address.street.trim() || !buyer.address.city.trim() || !buyer.address.country.trim()) {
        return t('posInvoice.errors.addressRequired', 'Street, city and country are required for business buyers.')
      }
      if (buyer.address.country === 'SA') {
        if (!buyer.address.building_number.trim() || !buyer.address.district.trim() || !buyer.address.postal_code.trim()) {
          return t('posInvoice.errors.saAddressRequired', 'Building number, district and postal code are required for Saudi addresses.')
        }
        if (!/^\d{5}$/.test(buyer.address.postal_code.trim())) {
          return t('posInvoice.errors.postalCode', 'Saudi postal code must be exactly 5 digits.')
        }
      }
    }
    for (const item of items) {
      if (!item.description.trim()) {
        return t('posInvoice.errors.itemDescription', 'Every item needs a description.')
      }
      if (!(parseFloat(item.qty) > 0)) {
        return t('posInvoice.errors.itemQty', 'Item quantity must be greater than zero.')
      }
      if (parseFloat(item.unit_price) < 0 || item.unit_price === '') {
        return t('posInvoice.errors.itemPrice', 'Item unit price must be zero or more.')
      }
    }
    if (recordPayment && !(parseFloat(payment.amount) > 0)) {
      return t('posInvoice.errors.paymentAmount', 'Payment amount must be greater than zero.')
    }
    return ''
  }

  const handleIssue = async () => {
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }
    setIsLoading(true)
    setError('')
    try {
      const payload = {
        clinic_id: clinicId,
        buyer_type: buyerType,
        buyer_phone: buyer.phone.trim() || undefined,
        buyer_name: buyerType === 'b2b' ? buyer.companyName.trim() : (buyer.name.trim() || undefined),
        items: items.map((item) => ({
          description: item.description.trim(),
          qty: parseFloat(item.qty),
          unit_price: parseFloat(item.unit_price),
          vat_rate: parseFloat(item.vat_rate) || 0,
          discount_percent: parseFloat(item.discount_percent) || 0,
        })),
      }
      if (buyerType === 'b2b') {
        if (!buyer.notVatRegistered) {
          payload.buyer_vat_number = buyer.vatNumber.trim()
        } else {
          payload.buyer_other_id = buyer.otherId.trim()
          payload.buyer_other_id_scheme = buyer.otherIdScheme
        }
        payload.buyer_address = {
          street: buyer.address.street.trim(),
          building_number: buyer.address.building_number.trim() || undefined,
          district: buyer.address.district.trim() || undefined,
          city: buyer.address.city.trim(),
          postal_code: buyer.address.postal_code.trim() || undefined,
          country: buyer.address.country.trim(),
        }
      }
      if (recordPayment) {
        payload.payment = {
          amount: parseFloat(payment.amount),
          payment_type_id: payment.payment_type_id ? parseInt(payment.payment_type_id, 10) : undefined,
        }
      }
      const response = await invoiceAPI.createPosInvoice(payload)
      setIssuedInvoice(response?.data?.data || null)
    } catch (err) {
      const message = err?.response?.data?.message
        || err?.response?.data?.errors?.buyer_address?.[0]
        || t('posInvoice.errors.issueFailed', 'Failed to issue the invoice. Please check the details and try again.')
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const downloadBlob = (blob, defaultFileName) => {
    const disposition = blob.headers?.['content-disposition']
    let fileName = defaultFileName
    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/)
      if (match) fileName = match[1]
    }
    const blobUrl = URL.createObjectURL(blob.data)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(blobUrl)
  }

  const handlePrint = async () => {
    if (!issuedInvoice?.id || printing) return
    setPrinting(true)
    try {
      const response = await pdfServices.generatePosInvoicePdf(issuedInvoice.id)
      downloadBlob(response, `invoice-${issuedInvoice.invoice_number}.pdf`)
    } catch (err) {
      console.error('Error generating POS invoice PDF:', err)
      setError(t('posInvoice.errors.printFailed', 'Failed to generate the PDF. Please try again.'))
    } finally {
      setPrinting(false)
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose()
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !isLoading) {
      onClose()
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isLoading])

  if (!isOpen) {
    return null
  }

  const renderBuyerFields = () => {
    if (buyerType === 'b2c') {
      return (
        <>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={labelStyle}>
                {t('posInvoice.buyer.phone', 'Phone (optional)')}
              </label>
              <PhoneInputWithCountryCode
                value={buyer.phone}
                onChange={(fullPhone) => setBuyer((prev) => ({ ...prev, phone: fullPhone }))}
                disabled={isLoading}
                placeholder="791234567"
                defaultCountryCode="+962"
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={labelStyle}>{t('posInvoice.buyer.name', 'Name (optional)')}</label>
              <input
                type="text"
                value={buyer.name}
                onChange={(e) => setBuyer((prev) => ({ ...prev, name: e.target.value }))}
                disabled={isLoading}
                placeholder={t('posInvoice.buyer.namePlaceholder', 'Buyer name')}
                style={inputStyle}
              />
            </div>
          </div>
        </>
      )
    }

    return (
      <>
        <div style={{ marginBottom: '14px' }}>
          <label style={labelStyle}>
            {t('posInvoice.buyer.companyName', 'Company name')} <span style={{ color: '#EF4444' }}>*</span>
          </label>
          <input
            type="text"
            value={buyer.companyName}
            onChange={(e) => setBuyer((prev) => ({ ...prev, companyName: e.target.value }))}
            disabled={isLoading}
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: '14px' }}>
          <label style={labelStyle}>
            {t('posInvoice.buyer.contactPhone', 'Contact phone')} <span style={{ color: '#EF4444' }}>*</span>
          </label>
          <input
            type="tel"
            value={buyer.phone}
            onChange={(e) => setBuyer((prev) => ({ ...prev, phone: e.target.value }))}
            disabled={isLoading}
            placeholder={t('posInvoice.buyer.phonePlaceholder', 'e.g. 05xxxxxxxx')}
            style={inputStyle}
          />
        </div>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            color: '#374151',
            marginBottom: '12px',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={buyer.notVatRegistered}
            onChange={(e) => setBuyer((prev) => ({ ...prev, notVatRegistered: e.target.checked }))}
            disabled={isLoading}
          />
          {t('posInvoice.buyer.notVatRegistered', 'Buyer is not VAT-registered')}
        </label>

        {!buyer.notVatRegistered ? (
          <div style={{ marginBottom: '14px' }}>
            <label style={labelStyle}>
              {t('posInvoice.buyer.vatNumber', 'VAT number')} <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <input
              type="text"
              value={buyer.vatNumber}
              onChange={(e) => setBuyer((prev) => ({ ...prev, vatNumber: e.target.value }))}
              disabled={isLoading}
              placeholder={t('posInvoice.buyer.vatNumberPlaceholder', '15-digit VAT number')}
              style={inputStyle}
            />
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
            <div style={{ flex: 2 }}>
              <label style={labelStyle}>
                {t('posInvoice.buyer.otherId', 'ID number')} <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <input
                type="text"
                value={buyer.otherId}
                onChange={(e) => setBuyer((prev) => ({ ...prev, otherId: e.target.value }))}
                disabled={isLoading}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{t('posInvoice.buyer.otherIdScheme', 'Scheme')}</label>
              <select
                value={buyer.otherIdScheme}
                onChange={(e) => setBuyer((prev) => ({ ...prev, otherIdScheme: e.target.value }))}
                disabled={isLoading}
                style={inputStyle}
              >
                {OTHER_ID_SCHEMES.map((scheme) => (
                  <option key={scheme} value={scheme}>{scheme}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div style={{ marginBottom: '14px' }}>
          <label style={{ marginBottom: '6px', ...labelStyle }}>
            {t('posInvoice.buyer.address', 'Address')} <span style={{ color: '#EF4444' }}>*</span>
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <input
              type="text"
              value={buyer.address.street}
              onChange={(e) => setAddressField('street', e.target.value)}
              disabled={isLoading}
              placeholder={t('posInvoice.buyer.street', 'Street')}
              style={inputStyle}
            />
            <input
              type="text"
              value={buyer.address.building_number}
              onChange={(e) => setAddressField('building_number', e.target.value)}
              disabled={isLoading}
              placeholder={t('posInvoice.buyer.buildingNumber', 'Building no.')}
              style={inputStyle}
            />
            <input
              type="text"
              value={buyer.address.district}
              onChange={(e) => setAddressField('district', e.target.value)}
              disabled={isLoading}
              placeholder={t('posInvoice.buyer.district', 'District')}
              style={inputStyle}
            />
            <input
              type="text"
              value={buyer.address.city}
              onChange={(e) => setAddressField('city', e.target.value)}
              disabled={isLoading}
              placeholder={t('posInvoice.buyer.city', 'City')}
              style={inputStyle}
            />
            <input
              type="text"
              value={buyer.address.postal_code}
              onChange={(e) => setAddressField('postal_code', e.target.value)}
              disabled={isLoading}
              placeholder={t('posInvoice.buyer.postalCode', 'Postal code')}
              style={inputStyle}
            />
            {countries.length ? (
              <select
                value={buyer.address.country}
                onChange={(e) => setAddressField('country', e.target.value)}
                disabled={isLoading}
                style={inputStyle}
              >
                {countries.map((c) => {
                  const code = c?.iso2 || c?.code || c?.iso_code || c?.country_code
                  const name = c?.name_en || c?.name || code
                  return <option key={code || name} value={code || ''}>{name}</option>
                })}
              </select>
            ) : (
              <input
                type="text"
                value={buyer.address.country}
                onChange={(e) => setAddressField('country', e.target.value.toUpperCase())}
                disabled={isLoading}
                placeholder={t('posInvoice.buyer.country', 'Country (ISO code)')}
                style={inputStyle}
              />
            )}
          </div>
        </div>
      </>
    )
  }

  const modalContent = (
    <div
      className="pos-invoice-modal"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
      }}
      onClick={handleBackdropClick}
    >
      {/* Hide native number-input spinners inside this modal */}
      <style>{`
        .pos-invoice-modal input[type="number"]::-webkit-outer-spin-button,
        .pos-invoice-modal input[type="number"]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .pos-invoice-modal input[type="number"] {
          -moz-appearance: textfield;
          appearance: textfield;
        }
      `}</style>
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '28px',
          maxWidth: '860px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          fontFamily: '"Red Hat Display", sans-serif',
          direction: isRTL ? 'rtl' : 'ltr',
        }}
        onClick={(e) => e.stopPropagation()}
        dir={i18n.dir()}
        lang={i18n.language}
      >
        {issuedInvoice ? (
          /* ---- Success view (US-18: print appears after issuance) ---- */
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                backgroundColor: '#ECFDF5',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px auto',
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#1F2937', margin: '0 0 8px 0' }}>
              {t('posInvoice.successTitle', 'Invoice issued')}
            </h3>
            <p style={{ fontSize: '15px', color: '#374151', margin: '0 0 24px 0', lineHeight: 1.5 }}>
              {t('posInvoice.successMessage', 'Invoice {{number}} was issued successfully.', { number: issuedInvoice.invoice_number })}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={onClose}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: '#374151',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: '"Red Hat Display", sans-serif',
                }}
              >
                {t('posInvoice.close', 'Close')}
              </button>
              <button
                onClick={handlePrint}
                disabled={printing}
                style={{
                  padding: '12px 24px',
                  backgroundColor: printing ? '#5EEAD4' : '#00D4B3',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: printing ? 'not-allowed' : 'pointer',
                  fontFamily: '"Red Hat Display", sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(0, 212, 179, 0.3)',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 6 2 18 2 18 9" />
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  <rect x="6" y="14" width="12" height="8" />
                </svg>
                {printing
                  ? t('posInvoice.printing', 'Preparing…')
                  : t('posInvoice.print', 'Print Invoice')}
              </button>
            </div>
          </div>
        ) : (
          /* ---- Issue form ---- */
          <>
            <div style={{ marginBottom: '20px', textAlign: isRTL ? 'right' : 'left' }}>
              <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#1F2937', margin: '0 0 16px 0' }}>
                {t('posInvoice.title', 'New Invoice')}
              </h3>

              {/* Every clinic can issue B2B: subscribed ones go through the
                  tax-authority clearance/reporting pipeline; unsubscribed ones
                  get a manual tax invoice (full buyer block on the PDF) that
                  the company uses for its own tax return. The backend only
                  reports when the clinic is subscribed. */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                {[
                  { key: 'b2c', label: t('posInvoice.b2cLabel', 'Walk-in (B2C)'), hint: t('posInvoice.b2cHint', 'Buyer details are optional — a phone number is enough') },
                  {
                    key: 'b2b',
                    label: t('posInvoice.b2bLabel', 'Business (B2B)'),
                    hint: invoiceSubscribed
                      ? t('posInvoice.b2bHint', 'Full buyer details for tax invoices (clearance)')
                      : t('posInvoice.b2bHintManual', 'Full buyer details for a manual tax invoice — not submitted to ZATCA/JoFotara'),
                  },
                ].map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setBuyerType(option.key)}
                    disabled={isLoading}
                    style={{
                      flex: 1,
                      textAlign: isRTL ? 'right' : 'left',
                      padding: '12px 14px',
                      borderRadius: '10px',
                      border: buyerType === option.key ? '2px solid #00D4B3' : '1px solid #D1D5DB',
                      backgroundColor: buyerType === option.key ? '#F0FDFA' : '#FFFFFF',
                      cursor: 'pointer',
                      fontFamily: '"Red Hat Display", sans-serif',
                    }}
                  >
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '3px' }}>
                      {option.label}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6B7280', lineHeight: 1.4 }}>{option.hint}</div>
                  </button>
                ))}
              </div>
              {!invoiceSubscribed && (
                <div style={{
                  marginBottom: '16px',
                  padding: '10px 14px',
                  borderRadius: '10px',
                  background: '#FFFBEB',
                  border: '1px solid #FDE68A',
                  fontSize: '13px',
                  color: '#92400E',
                  lineHeight: 1.5,
                  textAlign: isRTL ? 'right' : 'left',
                }}>
                  {t('posInvoice.notSubscribedBanner', 'This clinic is not connected to e-invoicing — the invoice is issued for your records only and is not reported to the tax authority.')}
                </div>
              )}
            </div>

            {/* Buyer fields */}
            <div style={{ marginBottom: '18px' }}>{renderBuyerFields()}</div>

            {/* Items editor */}
            <div style={{ marginBottom: '18px' }}>
              <div style={{ ...labelStyle, fontWeight: 600, marginBottom: '8px' }}>
                {t('posInvoice.itemsTitle', 'Items')}
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 54px 88px 66px 160px 84px 32px',
                gap: '8px',
                fontSize: '11px',
                textTransform: 'uppercase',
                color: '#6B7280',
                marginBottom: '6px',
                fontWeight: 700,
              }}>
                <span>{t('posInvoice.items.description', 'Description')}</span>
                <span>{t('posInvoice.items.qty', 'Qty')}</span>
                <span>{t('posInvoice.items.unitPrice', 'Unit price')}</span>
                <span>{t('posInvoice.items.vat', 'VAT %')}</span>
                <span>{t('posInvoice.items.discount', 'Discount')}</span>
                <span style={{ textAlign: 'right' }}>{t('posInvoice.items.total', 'Total')}</span>
                <span />
              </div>
              {items.map((item, index) => (
                <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 54px 88px 66px 160px 84px 32px', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                  <ServicePicker
                    value={item.description}
                    services={services}
                    isLoading={isLoading}
                    disabled={isLoading}
                    t={t}
                    onChange={(val) => updateItem(index, 'description', val)}
                    onPick={(s) => setItems((prev) => prev.map((it, i) => (i === index ? {
                      ...it,
                      description: s.label,
                      unit_price: String(s.price ?? ''),
                      vat_rate: String(s.vat ?? 0),
                      discount_percent: '0',
                      discount_amount: '0',
                    } : it)))}
                  />
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={item.qty}
                    onChange={(e) => onQtyChange(index, e.target.value)}
                    disabled={isLoading}
                    style={inputStyle}
                    {...numericInputHandlers}
                  />
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={item.unit_price}
                    readOnly
                    title={t('posInvoice.items.catalogLocked', 'Price comes from the service catalog')}
                    style={lockedFieldStyle}
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="any"
                    value={item.vat_rate}
                    readOnly
                    title={t('posInvoice.items.catalogLocked', 'Price comes from the service catalog')}
                    style={lockedFieldStyle}
                  />
                  {/* Dual discount: amount ⇄ percentage stay in sync */}
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={item.discount_amount}
                      onChange={(e) => onDiscountAmountChange(index, e.target.value)}
                      disabled={isLoading}
                      placeholder={currency}
                      title={t('posInvoice.items.discountAmount', 'Discount amount')}
                      style={{ ...inputStyle, flex: 1, minWidth: 0, padding: '8px 6px', fontSize: '12px' }}
                      {...numericInputHandlers}
                    />
                    <input
                      type="number"
                      min="0"
                      max="99.9"
                      step="any"
                      value={item.discount_percent}
                      onChange={(e) => onDiscountPercentChange(index, e.target.value)}
                      disabled={isLoading}
                      placeholder="%"
                      title={t('posInvoice.items.discountPercent', 'Discount percentage')}
                      style={{ ...inputStyle, flex: 1, minWidth: 0, padding: '8px 6px', fontSize: '12px' }}
                      {...numericInputHandlers}
                    />
                  </div>
                  <span style={{ textAlign: 'right', fontSize: '13px', fontWeight: 700, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>
                    {lineTotal(item).toFixed(2)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    disabled={isLoading || items.length === 1}
                    title={t('posInvoice.removeItem', 'Remove item')}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      border: '1px solid #FECACA',
                      backgroundColor: items.length === 1 ? '#F9FAFB' : '#FEF2F2',
                      color: items.length === 1 ? '#D1D5DB' : '#EF4444',
                      cursor: items.length === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      lineHeight: 1,
                      padding: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addItem}
                disabled={isLoading}
                style={{
                  padding: '7px 14px',
                  backgroundColor: 'transparent',
                  color: '#00A896',
                  border: '1px dashed #00D4B3',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: '"Red Hat Display", sans-serif',
                }}
              >
                + {t('posInvoice.addItem', 'Add item')}
              </button>

              {/* Live totals */}
              <div style={{
                marginTop: '12px',
                borderTop: '1px solid #E5E7EB',
                paddingTop: '10px',
                display: 'flex',
                justifyContent: 'flex-end',
              }}>
                <table style={{ minWidth: '200px', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '3px 8px', color: '#4B5563' }}>{t('posInvoice.totals.subtotal', 'Subtotal')}</td>
                      <td style={{ padding: '3px 0', textAlign: 'right', fontWeight: 600 }}>
                        {totals.subtotal.toFixed(2)} {currency}
                      </td>
                    </tr>
                    {totals.discount > 0 && (
                      <tr>
                        <td style={{ padding: '3px 8px', color: '#4B5563' }}>{t('posInvoice.totals.discount', 'Discount')}</td>
                        <td style={{ padding: '3px 0', textAlign: 'right', fontWeight: 600, color: '#DC2626' }}>
                          − {totals.discount.toFixed(2)} {currency}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td style={{ padding: '3px 8px', color: '#4B5563' }}>{t('posInvoice.totals.vat', 'VAT')}</td>
                      <td style={{ padding: '3px 0', textAlign: 'right', fontWeight: 600 }}>
                        {totals.vat.toFixed(2)} {currency}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '5px 8px', color: '#111827', fontWeight: 700, borderTop: '2px solid #111827' }}>
                        {t('posInvoice.totals.total', 'Total')}
                      </td>
                      <td style={{ padding: '5px 0', textAlign: 'right', fontWeight: 700, borderTop: '2px solid #111827' }}>
                        {totals.total.toFixed(2)} {currency}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Optional immediate payment */}
            <div style={{ marginBottom: '18px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  color: '#374151',
                  cursor: 'pointer',
                  marginBottom: recordPayment ? '10px' : 0,
                }}
              >
                <input
                  type="checkbox"
                  checked={recordPayment}
                  onChange={(e) => setRecordPayment(e.target.checked)}
                  disabled={isLoading}
                />
                {t('posInvoice.recordPayment', 'Record payment now')}
              </label>
              {recordPayment && (
                <div style={{ display: 'flex', gap: '10px', paddingInlineStart: '24px' }}>
                  <div style={{ flex: 1 }}>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={payment.amount}
                      onChange={(e) => setPayment((prev) => ({ ...prev, amount: e.target.value }))}
                      disabled={isLoading}
                      placeholder={t('posInvoice.paymentAmount', 'Amount')}
                      style={inputStyle}
                      {...numericInputHandlers}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <select
                      value={payment.payment_type_id}
                      onChange={(e) => setPayment((prev) => ({ ...prev, payment_type_id: e.target.value }))}
                      disabled={isLoading || loadingPaymentTypes}
                      style={inputStyle}
                    >
                      <option value="">{t('posInvoice.paymentMethod', 'Payment method')}</option>
                      {paymentTypes.map((type) => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div style={{
                marginBottom: '16px',
                padding: '10px 14px',
                backgroundColor: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: '8px',
                color: '#991B1B',
                fontSize: '13px',
                lineHeight: 1.5,
              }}>
                {error}
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                onClick={onClose}
                disabled={isLoading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: isLoading ? '#9CA3AF' : '#374151',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 500,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontFamily: '"Red Hat Display", sans-serif',
                }}
              >
                {t('posInvoice.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleIssue}
                disabled={isLoading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: isLoading ? '#5EEAD4' : '#00D4B3',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontFamily: '"Red Hat Display", sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(0, 212, 179, 0.3)',
                }}
              >
                {isLoading && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #FFFFFF',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }} />
                )}
                {t('posInvoice.issue', 'Issue Invoice')}
              </button>
            </div>
          </>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default PosInvoiceModal
